import {Injectable} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {HttpClient, HttpParams} from '@angular/common/http';
import {Observable, of, Subject} from 'rxjs';
import {catchError, map, tap} from 'rxjs/internal/operators';
import {ApiRoutes} from '../../../config/api-routes';
import {InterventionTeam} from '../model/intervention-team';
import {InterventionTeamDetails} from '../model/intervention-team-details';
import {InterventionTeamContact} from '../model/intervention-team-contact';
import {ChappListLoadResponse} from '@npm.chapps.com/chapps.common.angular.library';
import {ActionType} from '../enums/enums';
import {PaginationResponse} from '../model/paginationResponse';

@Injectable()
export class InterventionTeamsService {
  change$ = new Subject<InterventionTeamDetails>();
  delete$ = new Subject<number>();

  constructor(private httpClient: HttpClient, private translate: TranslateService, private config: ApiRoutes) {
  }

  public getTeams(): Observable<InterventionTeam[]> {
    return this.httpClient.get<InterventionTeam[]>(this.config.INTERVENTION_TEAMS).pipe(
      map(teams => teams.map(dtoToModel)),
      catchError(error => {
        return of([]);
      })
    );
  }

  public getTeamSummaries(httpParams: HttpParams, pageNumber: number, pageSize: number): Observable<PaginationResponse<InterventionTeam>> {
    const params = httpParams
      .append('pageNumber', pageNumber.toString())
      .append('pageSize', pageSize.toString());
    return this.httpClient.get<PaginationResponse<InterventionTeam>>(this.config.INTERVENTION_TEAMS_SUMMARIES, {params}).pipe(
      catchError(error => {
        return of({items: [], page: 0, total: 0} as PaginationResponse<InterventionTeam>);
      })
    );
  }

  public getTeamsForBuildings(buildingGuids: string[]): Observable<PaginationResponse<InterventionTeam>> {
    return this.httpClient
      .post<PaginationResponse<InterventionTeam>>(
        this.config.INTERVENTION_TEAMS_SEARCH,
        { buildingGuids: buildingGuids }
      )
      .pipe(
        catchError(error => {
          return of({ items: [] } as PaginationResponse<InterventionTeam>);
        }
        )
      );
  }

  public getDefaultTeamsForBuilding(buildingGuid: string): Observable<Record<ActionType, InterventionTeam>> {
    const url = ApiRoutes.getRouteWithInjectedParams(this.config.INTERVENTION_TEAMS_DEFAULTS_FOR_BUILDING, [buildingGuid]);

    return this.httpClient
      .get<Record<ActionType, InterventionTeam>>(url)
      .pipe(
        catchError(error => {
          return of({} as Record<ActionType, InterventionTeam>);
        }
        )
      );
  }

  public updateDefaultTeamsForBuilding(buildingGuid: string, actionType: ActionType, interventionTeamId: string): Observable<any> {
    const url = ApiRoutes.getRouteWithInjectedParams(this.config.INTERVENTION_TEAMS_DEFAULTS_FOR_BUILDING, [buildingGuid]);
    return this.httpClient
      .post<Record<ActionType, InterventionTeam>>(url, { actionType, interventionTeamId })
      .pipe(
        catchError(error => {
          return of();
        }
        )
      );
  }

  public getDetails(teamId: string): Observable<InterventionTeamDetails> {
    const url = ApiRoutes.getRouteWithInjectedParams(this.config.INTERVENTION_TEAM, [teamId]);
    return this.httpClient.get<InterventionTeamDetails>(url).pipe(
      catchError(error => {
        return of(null);
      })
    );
  }

  public addInterventionTeam(team: InterventionTeamDetails): Observable<any> {
    return this.httpClient.post<any>(this.config.INTERVENTION_TEAMS, team)
      .pipe(
        catchError(error => {
          return of({});
        })
      );
  }

  public updateTeamDetails(value: InterventionTeamDetails): Observable<boolean> {
    return this.httpClient.put<boolean>(this.config.INTERVENTION_TEAMS, value)
      .pipe(
        tap(() => this.change$.next(value)),
        catchError(error => {
          return of(true);
        })
      );
  }

  public deleteInterventionTeam(teamId: string): Observable<any> {
    const url = ApiRoutes.getRouteWithInjectedParams(this.config.INTERVENTION_TEAM, [teamId]);
    return this.httpClient.delete(url)
      .pipe(
        catchError(error => {
          return of(true);
        })
      );
  }

  public deleteTeamContact(teamId: string, contactId: string): Observable<any> {
    const url = ApiRoutes.getRouteWithInjectedParams(this.config.INTERVENTION_TEAM_CONTACT, [teamId, contactId]);
    return this.httpClient.delete(url)
      .pipe(
        catchError(error => {
          return of(true);
        })
      );
  }

  public addTeamContact(teamId: string, contactId: string): Observable<InterventionTeamContact> {
    const url = ApiRoutes.getRouteWithInjectedParams(this.config.INTERVENTION_TEAM_CONTACT, [teamId, contactId]);
    return this.httpClient.post<InterventionTeamContact>(url, null);
  }

  public getContactInterventionTeams(contactId: string): Observable<ChappListLoadResponse<any>> {
    return this.httpClient.post<ChappListLoadResponse<any>>(
      this.config.INTERVENTION_TEAMS_SEARCH,
      { contactId }
    );
  }

  public addBuildingToInterventionTeam(interventionTeamId: string, buildingGuid: string) {
    const url = ApiRoutes.getRouteWithInjectedParams(
      this.config.INTERVENTION_TEAM_BUILDING,
      [interventionTeamId, buildingGuid],
    );

    return this.httpClient.post(url, {})
      .pipe(
        catchError(() => {
          return of(true);
        })
      );
  }

  public removeBuildingFromInterventionTeam(interventionTeamId: string, buildingGuid: string) {
    const url = ApiRoutes.getRouteWithInjectedParams(
      this.config.INTERVENTION_TEAM_BUILDING,
      [interventionTeamId, buildingGuid],
    );

    return this.httpClient.delete(url, {})
      .pipe(
        catchError(() => {
          return of(true);
        })
      );
  }
}

type InterventionTeamDTO = Omit<InterventionTeam, 'preferredEmail'>;

function dtoToModel(team: InterventionTeamDTO): InterventionTeam {
  return Object.assign(Object.create(InterventionTeam.prototype), team);
}
